/**
 * Warning Modal Component
 *
 * Displays warnings from backend sources (validation errors, etc.).
 * Shows severity icons, source attribution, and dismissible buttons.
 * Includes DialogDescription for a11y compliance.
 */

import { AlertTriangle, XCircle, Info, X } from 'lucide-react';

// Source name mapping - normalize backend source names for display
// "ERPNext" -> "System", etc.
const normalizeSource = (source: string | undefined): string | undefined => {
  if (!source) return undefined;
  // Map internal/technical names to user-friendly names
  const sourceMap: Record<string, string> = {
    'ERPNext': 'System',
    'erpnext': 'System',
    'Frappe': 'System',
    'frappe': 'System',
  };
  return sourceMap[source] || source;
};
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useWarning } from './useWarning';
import type { Warning, WarningSeverity } from './types';

// Severity configuration for icons and colors
const severityConfig: Record<WarningSeverity, {
  icon: typeof AlertTriangle;
  iconClass: string;
  bgClass: string;
  borderClass: string;
}> = {
  warning: {
    icon: AlertTriangle,
    iconClass: 'text-amber-500',
    bgClass: 'bg-amber-50 dark:bg-amber-950/20',
    borderClass: 'border-amber-200 dark:border-amber-800',
  },
  error: {
    icon: XCircle,
    iconClass: 'text-red-500',
    bgClass: 'bg-red-50 dark:bg-red-950/20',
    borderClass: 'border-red-200 dark:border-red-800',
  },
  info: {
    icon: Info,
    iconClass: 'text-blue-500',
    bgClass: 'bg-blue-50 dark:bg-blue-950/20',
    borderClass: 'border-blue-200 dark:border-blue-800',
  },
};

interface WarningItemProps {
  warning: Warning;
  onDismiss: (id: string) => void;
}

function WarningItem({ warning, onDismiss }: WarningItemProps) {
  const config = severityConfig[warning.severity];
  const Icon = config.icon;

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-sm border ${config.bgClass} ${config.borderClass}`}
    >
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${config.iconClass}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {warning.title}
          </span>
          {warning.source && normalizeSource(warning.source) && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {normalizeSource(warning.source)}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {warning.message}
        </p>
      </div>
      {warning.dismissable && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDismiss(warning.id)}
          className="flex-shrink-0 h-8 w-8 p-0"
        >
          <XCircle className="w-4 h-4" />
          <span className="sr-only">Dismiss</span>
        </Button>
      )}
    </div>
  );
}

export function WarningModal() {
  const { warnings, dismissWarning, dismissAll } = useWarning();

  const isOpen = warnings.length > 0;
  const hasMultiple = warnings.length > 1;

  // Don't render anything if no warnings
  if (!isOpen) {
    return null;
  }

  // Get the primary severity for the modal header
  const primarySeverity = warnings.some(w => w.severity === 'error')
    ? 'error'
    : warnings.some(w => w.severity === 'warning')
      ? 'warning'
      : 'info';

  const primaryConfig = severityConfig[primarySeverity];
  const PrimaryIcon = primaryConfig.icon;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && dismissAll()}>
      <DialogContent className="max-w-lg [&>button]:hidden">
        <DialogHeader className="flex flex-row items-center gap-3">
          <div className={`p-2 rounded-full flex-shrink-0 ${primaryConfig.bgClass}`}>
            <PrimaryIcon className={`w-5 h-5 ${primaryConfig.iconClass}`} />
          </div>
          <div>
            <DialogTitle>
              {hasMultiple
                ? `${warnings.length} Warnings`
                : warnings[0]?.title || 'Warning'}
            </DialogTitle>
            <DialogDescription>
              {hasMultiple
                ? 'The following issues require your attention. The action was not applied.'
                : 'Please review the following issue. The action was not applied.'}
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {warnings.map((warning) => (
            <WarningItem
              key={warning.id}
              warning={warning}
              onDismiss={dismissWarning}
            />
          ))}
        </div>

        <DialogFooter className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={dismissAll}
            className="h-8 w-8 p-0 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
